import React, { useCallback, useMemo } from 'react';
import { Button, ButtonGroup, rem, Spacer } from 'design-system';
import Text from 'design-system/Text';
import useStyles from 'hooks/useStyles';
import { View } from 'react-native';
import createStyleSheets from 'utils/createStyleSheets';
import Amount from 'design-system/Amount';
import IconCircle from 'design-system/icons/IconCircle';
import IconTransactionCategory from 'features/transactions/icons/IconTransactionCategory';
import useColors from 'hooks/useColors';
import useShouldShowUpgrade from 'features/premium/hooks/useShouldShowUpgrade';
import { isWeb } from '../../../constants';
const strings = {
    subDescription: 'We recommend keeping the budget and aiming to spend less this period since you’ve overspent in the previous.',
};
const EditBudgetsCategoryModalContent = ({ showSubDescription, category, explainZeroBudget, confirmLabel, cancelLabel, confirmHandler, cancelHandler, showEditingText = true, isToolTip = false, customIcon, }) => {
    const { displayName, rollingAccumulatedLimit, currency, totalLimit, key, emoji, previousPeriodAverage, baseLimit, } = category;
    const styles = useStyles(styleSet);
    const colors = useColors();
    const { isUnlockedForSpace } = useShouldShowUpgrade({
        benefitId: 'rollingBudgets',
    });
    const description = useCallback(() => `Current ${displayName || ''} budget includes both the default amount and rolled-over from the last period. Editing will reset the rollover, making the new ${displayName || ''} budget your default.`, [displayName]);
    const txnCategory = useMemo(() => {
        const id = key?.split('.')[1];
        return {
            id: id,
            emoji: emoji ?? undefined,
        };
    }, [emoji, key]);
    const showTotalOrRollingLimit = useMemo(() => (Number(rollingAccumulatedLimit) || 0) + (Number(totalLimit) || 0) <= 0
        ? 0
        : (Number(rollingAccumulatedLimit) || 0) + (Number(totalLimit) || 0), [rollingAccumulatedLimit, totalLimit]);
    const rollingPlusBase = useMemo(() => (Number(rollingAccumulatedLimit) || 0) + (Number(baseLimit) || 0), [baseLimit, rollingAccumulatedLimit]);
    const descriptionZeroBudget = useCallback(() => (<View>
        <Text TextThin-14 Secondary {...(isWeb ? {} : { centered: true })}>
          {`You rolled over `}
          <Amount NumbersThin-14 Secondary value={{
            value: rollingAccumulatedLimit || 0,
            currency,
        }}/>
          {` from last period. With a `}
          <Amount NumbersThin-14 Secondary value={{
            value: baseLimit || 0,
            currency,
        }}/>
          {` default budget, this period’s budget is `}
          <Amount NumbersThin-14 Secondary value={{
            value: showTotalOrRollingLimit,
            currency,
        }}/>
          {`. The remaining `}
          <Amount NumbersThin-14 Secondary value={{
            value: rollingPlusBase,
            currency,
        }}/>
          {` will be carried over to the next period.`}
        </Text>
        {showEditingText ? (<>
            <Spacer h={8}/>
            <Text TextThin-14 Secondary {...(isWeb ? {} : { centered: true })}>
              {`Editing will reset the rollover, making the new ${displayName || ''} budget your default.`}
            </Text>
          </>) : null}
      </View>), [
        baseLimit,
        currency,
        displayName,
        rollingAccumulatedLimit,
        rollingPlusBase,
        showEditingText,
        showTotalOrRollingLimit,
    ]);
    const zeroBudgetBreakdown = useMemo(() => (<View style={styles.container}>
        <View style={styles.textRow}>
          <Text TextThin-14 Secondary>
            {`Default budget:`}
          </Text>
          <Amount NumbersThin-14 Secondary value={{
            value: Number(baseLimit) || 0,
            currency,
        }}/>
        </View>
        <Spacer h={4}/>
        <View style={styles.textRow}>
          <Text TextThin-14 Secondary>
            {`Rolled over from last period:`}
          </Text>
          <Amount NumbersThin-14 showSign Secondary value={{
            value: Number(rollingAccumulatedLimit),
            currency,
        }}/>
        </View>
        <Spacer h={8}/>
        <View style={styles.divider}/>
        <Spacer h={8}/>

        <View style={styles.textRow}>
          <Text TextThin-14 Secondary>
            {`Current budget:`}
          </Text>
          <Amount NumbersThin-14 Secondary value={{
            value: showTotalOrRollingLimit,
            currency,
        }}/>
        </View>
        <Spacer h={4}/>
        <View style={styles.textRow}>
          <Text TextThin-14 Secondary>
            {`To roll over next period:`}
          </Text>
          <Amount NumbersThin-14 showSign Secondary value={{
            value: rollingPlusBase,
            currency,
        }}/>
        </View>
        <Spacer h={16}/>
      </View>), [
        styles.container,
        styles.textRow,
        styles.divider,
        baseLimit,
        currency,
        rollingAccumulatedLimit,
        showTotalOrRollingLimit,
        rollingPlusBase,
    ]);
    const displayIcon = customIcon || (<IconTransactionCategory category={txnCategory} lightBackground/>);
    const renderAmountRow = useMemo(() => {
        if (rollingAccumulatedLimit) {
            return (<View>
          <Text TextThin-14 Secondary>
            {`Default `}
            <Amount Numbers-14 Secondary value={{
                    value: baseLimit || 0,
                    currency,
                }}/>
          </Text>

          {!isUnlockedForSpace ? <Spacer h={2}/> : null}
          <Text TextThin-14 {...(!isUnlockedForSpace ? { Gray: true } : {})} color={rollingAccumulatedLimit >= 0
                    ? colors.status.positive
                    : colors.status.negative}>
            {`Rolled `}
            <Amount forceScramble={!isUnlockedForSpace} Numbers-14 showSign color={rollingAccumulatedLimit >= 0
                    ? colors.status.positive
                    : colors.status.negative} value={{
                    value: rollingAccumulatedLimit,
                    currency,
                }}/>
          </Text>
        </View>);
        }
        if (previousPeriodAverage) {
            return (<Text TextThin-14 Gray>
          {`3-months avg `}
          <Amount Numbers-14 Secondary value={{
                    value: previousPeriodAverage,
                    currency,
                }}/>
        </Text>);
        }
        return null;
    }, [
        baseLimit,
        colors.status.negative,
        colors.status.positive,
        currency,
        isUnlockedForSpace,
        previousPeriodAverage,
        rollingAccumulatedLimit,
    ]);
    return (<View style={[
            isToolTip ? styles.toolTipNegativeMargin : undefined,
            styles.contentContainer,
        ]}>
      <View>
        {explainZeroBudget && isUnlockedForSpace ? (<View>
            {descriptionZeroBudget()}
            {zeroBudgetBreakdown}
          </View>) : (<View>
            <Text TextThin-14 Secondary {...(isWeb ? {} : { centered: true })}>
              {description()}
            </Text>
            <View style={styles.renderItem}>
              <View style={[styles.container2, styles.modalRenderItem]}>
                <IconCircle width={44} height={44} color={colors.cards.onDark}>
                  {displayIcon}
                </IconCircle>
                <View style={styles.content}>
                  <View style={styles.flex}>
                    <Text Text-16>{displayName}</Text>
                    {renderAmountRow}
                  </View>
                  <Amount Numbers-20 value={{ value: totalLimit || 0, currency }} style={styles.textInput}/>
                </View>
              </View>
            </View>
          </View>)}

        {showSubDescription && isUnlockedForSpace ? (<View style={styles.subDescription}>
            <Text TextThin-14 Secondary {...(isWeb ? {} : { centered: true })}>
              {strings.subDescription}
            </Text>
          </View>) : null}
        {!!isWeb && <Spacer.H8 />}
      </View>

      {isWeb ? (<ButtonGroup margins={false}>
          {cancelLabel && cancelHandler ? (<Button brandReversed title={cancelLabel} onPress={cancelHandler}/>) : null}

          {confirmLabel && confirmHandler ? (<Button brand title={confirmLabel} onPress={confirmHandler}/>) : null}
        </ButtonGroup>) : null}
    </View>);
};
export default EditBudgetsCategoryModalContent;
const styleSet = createStyleSheets((colors) => ({
    renderItem: {
        marginTop: rem(16),
    },
    subDescription: {
        marginTop: rem(16),
    },
    container: {
        marginTop: rem(16),
        padding: rem(16),
        backgroundColor: colors.background.dark,
        borderRadius: rem(16),
    },
    container2: {
        padding: rem(16),
        flexDirection: 'row',
        alignItems: 'center',
        ...(isWeb && {
            paddingHorizontal: rem(8),
        }),
    },
    textRow: {
        flexDirection: 'row',
        justifyContent: 'space-between',
    },
    modalRenderItem: {
        backgroundColor: colors.background.dark,
        borderRadius: rem(16),
    },
    content: {
        flexDirection: 'row',
        flex: 1,
        alignItems: 'center',
        justifyContent: 'space-between',
        marginStart: rem(16),
    },
    flex: {
        flex: 1,
    },
    divider: {
        borderTopColor: colors.borders.divider,
        borderTopWidth: rem(1),
    },
    textInput: {
        fontFamily: 'Nunito-Bold',
        fontSize: rem(20),
        color: colors.text.primary,
        paddingLeft: rem(16),
        paddingVertical: rem(8),
        ...(isWeb && {
            width: rem(150),
            textAlign: 'right',
            backgroundColor: colors.background.dark,
        }),
    },
    toolTipNegativeMargin: {
        marginBottom: rem(-16),
    },
    contentContainer: {
        width: '100%',
    },
}));
